<script setup>
import {onMounted, onBeforeUnmount, reactive, ref, watch, computed, nextTick} from 'vue';
import axios from 'axios';
import { useAuthStore } from '@stores/auth';
import router from '@/router/index.js';
import { url } from '@/config/urls';
import { useHeaderStore } from '@stores/header';
import { useSideMenuStore } from '@stores/sidemenu';

const auth = useAuthStore();
const header = useHeaderStore();
const sideMenuStore = useSideMenuStore();

const isAuthenticated = computed(() => auth.isAuthenticated);

// グラデーション背景とコンテンツ開始位置をヘッダー高さに連動させる
let resizeObserver = null;

// コンテンツ量に応じてヘッダー高さが変わるため、描画後に再計測する
const updateHeaderOffset = () => {
  const headerEl = document.getElementById('com_app_header1');
  if (!headerEl) return;
  // スクロール位置の影響を受けないようにする
  const marginBottom = parseFloat(getComputedStyle(headerEl).marginBottom);
  document.documentElement.style.setProperty('--header-offset', `${headerEl.offsetTop + headerEl.offsetHeight + marginBottom}px`);
};

onMounted(() => {
  if (auth.isAuthenticated) {
    getUserInfo()
  }
  const headerEl = document.getElementById('com_app_header1');
  if (headerEl) {
    resizeObserver = new ResizeObserver(updateHeaderOffset);
    resizeObserver.observe(headerEl);
    updateHeaderOffset();
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

// ユーザー情報
const user = reactive({
  customerUnityCode: '',
  email: '',
  emailStatus: true,
  formalName1: '',
  formalName2: ''
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = () => {
  // URLが設定されていない場合は実行しない
  if (!props.userDataUrl) {
    return;
  }
  axios.get(props.userDataUrl)
    .then(response => {
      user.customerUnityCode = response.data.response.customerUnityCode;
      user.email = response.data.response.email;
      user.emailStatus = response.data.response.emailStatus;
      user.formalName1 = response.data.response.formalName1 ?? '';
      user.formalName2 = response.data.response.formalName2 ?? '';
      nextTick(updateHeaderOffset);
    })
    .catch(error => {
      const status = error.response?.status;
      switch(error?.response?.status) {
        case 401:
          router.push('/login');
          break;
        case 403:
          router.push({
            path: '/error',
            query: {
              errorCode: '403',
              title: 'アクセス権限がありません',
              explanation: 'このページを表示する権限がありません。'
            }
          });
          break;
        case 404:
          router.push({
            path: '/error',
            query: {
              errorCode: '404',
              message: error.response?.data?.message
            }
          });
          break;
        case 500:
        case 503:
          router.push({
            path: '/error',
            query: {
              errorCode: status,
              message: error.response?.data?.message
            }
          });
          break;
        default:
          router.push({
            path: '/error',
            query: {
              errorCode: status || 'unknown',
              title: 'エラーが発生しました',
              explanation: error.response?.data?.message || 'エラーが発生しました。しばらくしてから再度お試しください。'
            }
          });
          break;
      }
    })
}

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  userDataUrl: {type: String, default: url.api.userData},
});

watch(
  () => header.emailChangeObserver
  , () => {
    if (isAuthenticated.value) {
      getUserInfo();
    }
});

watch(
  () => header.verifiedChangeObserver
  , () => {
    if(isAuthenticated.value) {
      getUserInfo();
    }
  }
);

const onClickMenuButton = () => {
  sideMenuStore.toggle()
}

</script>

<template>
  <div class="com_bg1"></div>
  <header id="com_app_header1">
      <h1 class="logo1">
        <span v-if="!isAuthenticated">共創未来ポータル</span>
        <router-link v-else to="/">共創未来ポータル</router-link>
      </h1>
      <div class="data1" v-if="user.formalName1 || user.formalName2">
        <p class="text1"><span>{{ user.formalName1 + ' ' + user.formalName2 + ' '}}</span>様</p>
        <p class="text2"><span>ご利用ID：{{ user.customerUnityCode }}</span><span>ご登録アドレス：{{ user.email }}</span></p>
      </div>
      <div id="com_app_menu_btn1">
        <a href="javascript:void(0)" @click.prevent="onClickMenuButton" :class="{ on: sideMenuStore.isSideMenuOpen }"></a>
        <span></span>
        <span class="txt1"></span>
      </div>
  </header>
</template>

<style scoped>
.unverified-label > a {
  color: red;
  text-decoration: underline;
}
</style>
